import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Beaker, Plus, X, Loader2, Link2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELATIONSHIP_CONFIG = {
  potential: { label: "Potential", emoji: "🔵", color: "bg-blue-100 text-blue-800" },
  current: { label: "Current", emoji: "🟢", color: "bg-emerald-100 text-emerald-800" },
  previous: { label: "Previous", emoji: "⚫", color: "bg-slate-100 text-slate-700" },
};

const StudyLinkCard = ({ study, onUnlink }) => (
  <div className="p-3 bg-white border border-slate-200 rounded-lg flex items-start justify-between gap-2" data-testid={`linked-study-${study.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Beaker className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
        <p className="font-medium text-sm text-slate-800 truncate">{study.study_name}</p>
        <Badge variant="outline" className="text-[10px]">{study.status}</Badge>
      </div>
      <div className="text-xs text-slate-500 mt-1 space-x-3">
        {study.protocol_number && <span>#{study.protocol_number}</span>}
        {study.sponsor && <span>· {study.sponsor}</span>}
        {study.therapeutic_area && <span>· {study.therapeutic_area}</span>}
      </div>
      <p className="text-xs text-slate-400 mt-1">@ {study.company_name}</p>
    </div>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onUnlink(study.id)}
      className="text-slate-400 hover:text-red-500 h-7 px-2"
      data-testid={`unlink-study-${study.id}`}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export const LeadStudyLinks = ({ leadId, companyName }) => {
  const [grouped, setGrouped] = useState({ potential: [], current: [], previous: [] });
  const [loading, setLoading] = useState(true);
  const [companyStudies, setCompanyStudies] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [selectedRel, setSelectedRel] = useState("potential");
  const [linking, setLinking] = useState(false);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads/${leadId}/studies`);
      setGrouped(res.data || { potential: [], current: [], previous: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyStudies = async () => {
    if (!companyName) return;
    try {
      const res = await axios.get(`${API}/companies/${encodeURIComponent(companyName)}/studies`);
      setCompanyStudies(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchLinks();
      fetchCompanyStudies();
    }
  }, [leadId, companyName]);

  const handleLink = async () => {
    if (!selectedStudyId) {
      toast.error("Select a study");
      return;
    }
    setLinking(true);
    try {
      await axios.post(`${API}/studies/${selectedStudyId}/leads`, {
        lead_id: leadId,
        relationship: selectedRel,
      });
      toast.success("Linked to study");
      setSelectedStudyId("");
      setShowAdd(false);
      await fetchLinks();
    } catch (e) {
      console.error(e);
      toast.error("Failed to link");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (studyId) => {
    try {
      await axios.delete(`${API}/studies/${studyId}/leads/${leadId}`);
      toast.success("Unlinked");
      await fetchLinks();
    } catch (e) {
      console.error(e);
      toast.error("Failed to unlink");
    }
  };

  const totalLinked = grouped.potential.length + grouped.current.length + grouped.previous.length;
  const alreadyLinkedIds = new Set([
    ...grouped.potential.map(s => s.id),
    ...grouped.current.map(s => s.id),
    ...grouped.previous.map(s => s.id),
  ]);
  const availableStudies = companyStudies.filter(s => !alreadyLinkedIds.has(s.id));

  return (
    <Card data-testid="lead-study-links-panel">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5 text-slate-400" />
              Linked Studies
              {totalLinked > 0 && <Badge variant="secondary" className="ml-1">{totalLinked}</Badge>}
            </CardTitle>
            <CardDescription>Trials/projects this contact is associated with</CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAdd(!showAdd)}
            data-testid="add-study-link-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Link Study
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showAdd && (
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <div>
              <Label className="text-xs">Study (from {companyName || "company"})</Label>
              <Select value={selectedStudyId} onValueChange={setSelectedStudyId}>
                <SelectTrigger className="mt-1" data-testid="select-study">
                  <SelectValue placeholder={availableStudies.length === 0 ? "No unlinked studies — add one on the company page first" : "Select a study"} />
                </SelectTrigger>
                <SelectContent>
                  {availableStudies.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.study_name} ({s.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Relationship</Label>
              <Select value={selectedRel} onValueChange={setSelectedRel}>
                <SelectTrigger className="mt-1" data-testid="select-relationship">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="potential">🔵 Potential</SelectItem>
                  <SelectItem value="current">🟢 Current</SelectItem>
                  <SelectItem value="previous">⚫ Previous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleLink} disabled={linking || !selectedStudyId} className="bg-[#2E7AD1] hover:bg-[#2563EB]" data-testid="confirm-link-study">
                {linking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                Link
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-400 py-4 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</div>
        ) : totalLinked === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No studies linked yet. Click &quot;Link Study&quot; above to associate this contact with trials.</p>
        ) : (
          <div className="space-y-4">
            {["current", "potential", "previous"].map((rel) => {
              const list = grouped[rel];
              if (list.length === 0) return null;
              const cfg = RELATIONSHIP_CONFIG[rel];
              return (
                <div key={rel}>
                  <div className="flex items-center gap-2 mb-2">
                    <span>{cfg.emoji}</span>
                    <h4 className="text-sm font-semibold text-slate-700">{cfg.label} Trials</h4>
                    <Badge variant="secondary" className="text-xs">{list.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {list.map((study) => (
                      <StudyLinkCard key={study.id} study={study} onUnlink={handleUnlink} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadStudyLinks;
